"use client"

import classNames from "classnames"
import { usePathname, useSearchParams } from "next/navigation"
import { useMemo } from "react"
import { Dropdown } from "antd"

import "./index.scss"

import { useLocale } from "next-intl"
import Link from "next/link"

import IconFont from "../../../IconFont"

export interface LanguageOption {
  label: string
  value: string // 站内为 locale，站外为完整 URL
  flag: string // 国旗 emoji 或图片 URL
}

// 支持的语言列表（站内 locale）
const SUPPORTED_LOCALES = ["en", "th", "id", "vn", "fr", "ru", "es", "pt"] as const

/** 判断是否为外站 URL（用于多语言跳转到独立站点） */
function isExternalLangUrl(value: string): boolean {
  return value.startsWith("http://") || value.startsWith("https://")
}

/**
 * 语言选择器组件
 * 支持多语言切换，带国旗图标
 */
export function LanguageSelector() {
  const locale = useLocale()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const isTest = useMemo(() => {
    if (process.env.NEXT_PUBLIC_ENV !== "production") {
      return "test"
    }
    return ""
  }, [])

  const Languages: LanguageOption[] = useMemo(() => {
    return [
      { label: "English", value: "en", flag: "icon-meiguoguoqi" },
      { label: "中文", value: `https://${isTest}www.smm.cn`, flag: "icon-icon-CNH" },
      { label: "แบบไทย", value: "th", flag: "icon-taiguo" },
      { label: "Bahasa", value: "id", flag: "icon-yinni" },
      { label: "Tiếng Việt", value: "vn", flag: "icon-yuenan" },
      { label: "Français", value: "fr", flag: "icon-faguo" },
      { label: "Россия", value: "ru", flag: "icon-eluosi" },
      { label: "Español", value: "es", flag: "icon-xibanya" },
      { label: "Português", value: "pt", flag: "icon-putaoya" },
    ]
  }, [isTest])

  // 构建带 locale 的路径
  const buildPathWithLocale = useMemo(() => {
    return (targetLocale: string): string => {
      // 规范化路径：移除首尾斜杠，分割路径段
      const normalizedPath = pathname.replace(/^\/+|\/+$/g, "")
      const segments = normalizedPath ? normalizedPath.split("/").filter(Boolean) : []

      // 判断第一个段是否是支持的语言代码
      const firstSegment = segments[0] || ""
      const isFirstSegmentLocale = SUPPORTED_LOCALES.includes(firstSegment as (typeof SUPPORTED_LOCALES)[number])

      // 提取剩余路径
      // 如果第一个段是 locale，则跳过它；否则保留所有段
      const remainingPath = isFirstSegmentLocale ? segments.slice(1) : segments

      // 构建新路径
      const newPath = remainingPath.length > 0 ? `/${targetLocale}/${remainingPath.join("/")}` : `/${targetLocale}`

      // 保留查询参数
      const queryString = searchParams.toString()
      return queryString ? `${newPath}?${queryString}` : newPath
    }
  }, [pathname, searchParams])

  const menuItems = useMemo(() => {
    return Languages.map((lang) => {
      const isExternal = isExternalLangUrl(lang.value)

      const linkClassName = classNames("languageMenuItem", {
        active: !isExternal && lang.value === locale,
      })

      return {
        key: lang.value,
        label: isExternal ? (
          <a
            href={lang.value}
            target="_blank"
            hrefLang={lang.value}
            className={linkClassName}
            rel="noopener noreferrer"
          >
            <IconFont
              type={lang.flag}
              className="languageFlag"
              width={20}
              height={14}
              style={{
                fontSize: 14,
                color: "#0c1019",
              }}
            />
            <span className="languageLabel">{lang.label}</span>
          </a>
        ) : (
          <Link
            replace
            prefetch={false}
            href={buildPathWithLocale(lang.value)}
            hrefLang={lang.value}
            className={linkClassName}
          >
            <IconFont
              type={lang.flag}
              className="languageFlag"
              width={20}
              height={14}
              style={{
                fontSize: 14,
                color: "#0c1019",
              }}
            />
            <span className="languageLabel">{lang.label}</span>
          </Link>
        ),
      }
    })
  }, [Languages, buildPathWithLocale, locale])

  // 获取当前语言选项，确保有默认值
  const getCurrentLangOption = (): LanguageOption => {
    const found = Languages.find((lang) => lang.value === locale)
    if (found) return found
    const firstLang = Languages[0]
    if (firstLang) return firstLang
    return { label: "EN", value: "en", flag: "icon-meiguoguoqi" }
  }

  const currentLangOption = getCurrentLangOption()

  return (
    <Dropdown
      menu={{
        items: menuItems,
      }}
      overlayClassName="languageDropdown languageDropdownGlobal"
    >
      <span className="languageLabel languageSelector">{currentLangOption.value.toUpperCase()}</span>
    </Dropdown>
  )
}
